import { useContext, useEffect, useState } from "react";
import {
  DatePicker,
  Select,
  Table,
  Row,
  Col,
  Switch,
  notification,
  Alert,
} from "antd";
import moment from "moment";
import dayjs from "dayjs/esm";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";

import "./styles.css";

import { Context } from "../../context";
import { Fetch } from "../../fetch";
import { convertIdToKey, handleExportClick } from "../../utils";
import ExportButton from "../ExportButton";
import {
  CheckCircleFilled,
  CloseCircleOutlined,
} from "@ant-design/icons";

dayjs.extend(weekday);
dayjs.extend(localeData);

const { Column } = Table;
const displayDateFormat = "YYYY-MM-DD HH:mm:ss";
const dateFormat = "YYYY-MM-DD";

const ManageOrders = () => {
  const { token, apiUrl } = useContext(Context);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({
    start_date: dayjs()
      .subtract(0, "month")
      .startOf("month")
      .format(displayDateFormat),
    end_date: dayjs()
      .subtract(0, "month")
      .endOf("month")
      .format(displayDateFormat),
    plan: null,
    paid: true,
    currency: null,
    order_type: "customer",
    reseller_id: null,
  });
  const [plans, setPlans] = useState([]);
  const [applePlans, setApplePlans] = useState([]);
  const [androidPlans, setAndroidPlans] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  const [allResellers, setAllResellers] = useState([]);

  const getAllResellers = () => {
    Fetch(`${apiUrl}/resellers`, token, (result) => {
      if ("resellers" in result) setAllResellers(result.resellers);
    });
  };

  const handleTableDataChange = (page) => {
    setLoading(true);
    let f = filters;
    let planstring = f.plan ? `plan=${f.plan}` : ``;
    let order_type = f.order_type ? `order_type=${f.order_type}` : "customer";
    let starting_date = filters.start_date;
    let ending_date = filters.end_date;
    let sdate = moment(starting_date).isValid()
      ? moment(starting_date).format(displayDateFormat)
      : null;
    let edate = moment(ending_date).isValid()
      ? moment(ending_date).format(displayDateFormat)
      : null;
    let datestring =
      sdate && edate ? `start_date=${sdate}&end_date=${edate}` : ``;
    let paidstring = f.paid ? `paid=${true}` : `paid=${false}`;
    let currencystring = f.currency ? `currency=${f.currency}` : ``;
    let resellerstring =
      f.order_type === "reseller" && f.reseller_id
        ? `reseller_id=${f.reseller_id}`
        : ``;
    let fstring = `${planstring}&${order_type}&${datestring}&${paidstring}&${currencystring}&${resellerstring}`;
    setQuery(fstring);
    Fetch(
      `${apiUrl}/orders?current=${page.current}&size=${page.pageSize}&${fstring}`,
      token,
      (result) => {
        const orders = convertIdToKey(result.orders);
        setOrders(orders);
        setLoading(false);
        setPagination((prev) => {
          return {
            ...page,
            total: result.total,
          };
        });
      }
    );
  };

  useEffect(() => {
    handleTableDataChange(pagination);
    if ("length" in plans && plans.length === 0) {
      Fetch(`${apiUrl}/plans?type=web`, token, (result) => {
        const plans = convertIdToKey(result.plans);
        setPlans(plans);
      });

      Fetch(`${apiUrl}/plans?type=apple`, token, (result) => {
        const plans = convertIdToKey(result.plans);
        plans.forEach((plan) => {
          plan.name = plan.planId;
          plan.enrichedName = plan.name + " (Apple)";
        });
        setApplePlans(plans);
      });

      Fetch(`${apiUrl}/plans?type=android`, token, (result) => {
        const plans = convertIdToKey(result.plans);
        plans.forEach((plan) => {
          plan.name = plan.planId;
          plan.enrichedName = plan.name + " (Android)";
        });
        setAndroidPlans(plans);
      });
    }
  }, [apiUrl, token, filters]);

  useEffect(() => {
    getAllResellers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, token]);

  useEffect(() => {
    let temp_plans = [];
    if (plans && plans.length > 0) {
      temp_plans = [...temp_plans, ...plans];
    }
    if (applePlans && applePlans.length > 0) {
      temp_plans = [...temp_plans, ...applePlans];
    }
    if (androidPlans && applePlans.length > 0) {
      temp_plans = [...temp_plans, ...androidPlans];
    }
    setAllPlans(temp_plans);
  }, [plans, applePlans, androidPlans]);

  const onChangeDate = (date, datestring) => {
    const [start_date, end_date] = [datestring[0], datestring[1]];
    const getStartOfDay = (start_date) => {
      const startOfDay = moment(start_date)
        .startOf("day")
        .format(displayDateFormat);
      return startOfDay;
    };
    const getEndOfDay = (end_date) => {
      const endOfDay = moment(end_date).endOf("day").format(displayDateFormat);
      return endOfDay;
    };
    setFilters({
      ...filters,
      start_date: start_date ? getStartOfDay(start_date) : null,
      end_date: end_date ? getEndOfDay(end_date) : null,
    });
  };

  const customRanges = {
    Today: [moment().startOf("day"), moment().endOf("day")],
    Yesterday: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")],
    "Last Week": [dayjs().subtract(7, "day"), dayjs()],
  };

  const recheckPayment = (id) => {
    setLoading(true);
    let url = `${apiUrl}/pporders/${id}`;
    if (id && id.includes("order_")) url = `${apiUrl}/orders/${id}`;
    Fetch(url, token, (result) => {
      if (result.paid) {
        notification.success({
          message: "Transaction Successful",
          description: "Order Successfully updated",
        });
        setOrders(
          orders.map((order) =>
            order.key === id ? { ...order, paid: true } : order
          )
        );
      } else {
        notification.info({
          message: "Transaction Failed",
          description: "For more details check the PG Dashboard",
        });
      }
    });
    setLoading(false);
  };

  return (
    <div className="manage-orders-wrapper">
      <Row style={{ marginBottom: "20px" }} align={"middle"}>
        <Col span={2}>
          {" "}
          Paid{" "}
          <Switch
            checked={filters.paid ? true : false}
            onChange={(checked) => {
              setFilters({ ...filters, paid: checked ? true : null });
              setPagination({ current: 1, pageSize: 10 });
            }}
          />
        </Col>
        <Col span={3}>
          <Select
            style={{ width: "100%" }}
            placeholder={"Filter by currency"}
            allowClear
            onChange={(value) => {
              setFilters({ ...filters, currency: value });
              setPagination({ current: 1, pageSize: 10 });
            }}
          >
            {["INR", "EUR", "USD", "SGD", "AED", "NPR", "MPR"].map((code) => (
              <Select.Option key={code} value={code}>
                {code}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col span={4}>
          <Select
            style={{ width: "100%" }}
            placeholder={"Filter by Plan"}
            showSearch
            value={filters.plan}
            allowClear
            onChange={(value) => {
              setFilters({ ...filters, plan: value, reseller: null });
              setPagination({ current: 1, pageSize: 10 });
            }}
          >
            {allPlans &&
              allPlans.map((plan, index) => {
                return (
                  <Select.Option key={index} value={plan.name}>
                    {plan.enrichedName ? plan.enrichedName : plan.name}
                  </Select.Option>
                );
              })}
          </Select>
        </Col>
        <Col span={4}>
          <Select
            style={{ width: "100%" }}
            placeholder={"Filter Orders"}
            value={filters.order_type}
            defaultValue={filters.order_type}
            onChange={(v) => {
              setFilters({ ...filters, order_type: v, reseller_id: null });
              setPagination({ current: 1, pageSize: 10 });
            }}
          >
            <Select.Option value="customer">Customer Orders</Select.Option>
            <Select.Option value="reseller">Reseller Orders</Select.Option>
          </Select>
        </Col>
        {filters.order_type === "reseller" && (
          <Col span={4}>
            <Select
              style={{ width: "100%" }}
              placeholder={"Filter by Reseller"}
              showSearch
              optionFilterProp="children"
              allowClear
              value={filters.reseller_id}
              onChange={(value) => {
                setFilters({ ...filters, reseller_id: value || null });
                setPagination({ current: 1, pageSize: 10 });
              }}
            >
              <Select.Option value={null}>All resellers</Select.Option>
              {allResellers &&
                allResellers.map((reseller) => (
                  <Select.Option key={reseller._id} value={reseller._id}>
                    {reseller.name || reseller.company}
                  </Select.Option>
                ))}
            </Select>
          </Col>
        )}
        <Col span={7}>
          <DatePicker.RangePicker
            ranges={customRanges}
            onChange={onChangeDate}
            format={dateFormat}
            defaultValue={[
              dayjs().subtract(0, "month").startOf("month"),
              dayjs().subtract(0, "month").endOf("month"),
            ]}
          />
        </Col>
      </Row>
      <Table
        dataSource={orders}
        pagination={pagination}
        onChange={handleTableDataChange}
        loading={loading}
      >
        <Column title="ID" dataIndex="key" key="key" align="center" />
        <Column title="User" dataIndex="user" key="language" align="center" />
        <Column
          title="Date"
          dataIndex="date"
          key="date"
          align="center"
          render={(_, record) => moment(record.date).format(displayDateFormat)}
        />
        <Column
          title="Amount"
          dataIndex="amount"
          key="amount"
          align="center"
          render={(_, record) => `${record.currency} ${record.amount}`}
        />
        <Column
          title="Discount Used"
          dataIndex="discountCode"
          key="discountCode"
          align="center"
          render={(code) => (code === "None" ? "-" : code)}
        />
        <Column
          title="Paid"
          dataIndex="paid"
          key="paid"
          align="center"
          render={(paid) =>
            paid ? <CheckCircleFilled /> : <CloseCircleOutlined />
          }
        />
        <Column
          title="Actions"
          align="center"
          render={(_, record) =>
            record.paid ? (
              "-"
            ) : (
              <a onClick={() => recheckPayment(record.key)} disabled={loading}>
                Recheck Status
              </a>
            )
          }
        />
      </Table>
      <ExportButton
        className="export-btn-only"
        onExport={() =>
          handleExportClick(apiUrl, token, `orders?${query}`, "Orders.csv")
        }
      />
    </div>
  );
};

export default ManageOrders;
